'use client';

// imports
import { useState, useRef, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {Upload, Camera, ImageIcon, X, Check, ShieldCheck, CheckCircle, Loader2, AlertCircle, FileImage, Scan, Shield, Aperture, RotateCcw, Lightbulb, Maximize2} from 'lucide-react';
import FlipCameraAndroidOutlinedIcon from '@mui/icons-material/FlipCameraAndroidOutlined';
import CameraRoundedIcon from '@mui/icons-material/CameraRounded';
import ScanLine from '@/components/ui/ScanLine';
import { API_URL } from '@/lib/api';


// types
type UploadState = 'idle' | 'dragging' | 'camera' | 'preview' | 'analyzing' | 'done' | 'error';
type ResultStatus = 'success' | 'fail' | null;

// constants
const ACCEPTED_FORMATS = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; 

export default function UploadCard({ tipsRef, setHighlightTips,}: {
  tipsRef: React.RefObject<HTMLDivElement | null>;
  setHighlightTips: React.Dispatch<React.SetStateAction<boolean>>;
}) {

  // router
  const router = useRouter();


  // state
  const [state, setState] = useState<UploadState>('idle');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileSize, setFileSize] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);
  
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [isSwitching, setIsSwitching] = useState(false);

  const [highlightPrivacy, setHighlightPrivacy] = useState(false);
  const [saveHistory, setSaveHistory] = useState(false);

  const [resultStatus, setResultStatus] = useState<ResultStatus>(null);
  const [dotCount, setDotCount] = useState(1);
  const [uploadSource, setUploadSource] = useState<'camera' | 'file' | null>(null);

  // refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const privacyRef = useRef<HTMLDivElement>(null);

  // derived
  const isAnalyzing = state === 'analyzing';
  const isOverlayActive = state === 'analyzing' || state === 'done' || state === 'error';
  const isFullscreenOverlay = isOverlayActive && window.innerWidth < 640;
  const hasImage = state === 'preview' || state === 'analyzing' || state === 'done';

  // utils
  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setStream(null);
  };

   // handlers
  const handleFile = useCallback((file: File) => {
    setError('');

    if (!ACCEPTED_FORMATS.includes(file.type)) {
      setError('Unsupported format. Please upload a JPG, PNG, or WebP image.');
      setState('error');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Image too large. Maximum file size is 10MB.');
      setState('error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
      setFileName(file.name);
      setFileSize((file.size / 1024 / 1024).toFixed(2) + ' MB');
      setState('preview');
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setState('idle');
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setState('dragging');
  }, []);

  const handleDragLeave = useCallback(() => {
    setState((prev) => prev === 'dragging' ? 'idle' : prev);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadSource('file');
      handleFile(file);
  }
  };

  const openCameraWithMode = async (mode: 'user' | 'environment') => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1280 },
          height: { ideal: 960 },
        },
        audio: false,
      });

      setUploadSource('camera');
      setStream(mediaStream);
      setState('camera');

    } catch (err) {
      console.error(err);
      setError('Camera access denied.');
      setState('error');
    }
  };

  const openCamera = () => {
    openCameraWithMode(facingMode);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (facingMode === 'user') {
      // mirror capture
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
      ctx.restore();
    } else {
      // normal capture
      ctx.drawImage(video, 0, 0);
    }

    const imageData = canvas.toDataURL('image/png');

    setImagePreview(imageData);
    setFileName('captured-image.png');
    setFileSize('Camera Capture');

    closeCamera();
    setState('preview');
  };

  const switchCamera = async () => {
    if (isSwitching) return;

    setIsSwitching(true);

    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';

    closeCamera();
    setFacingMode(newFacingMode);

    await new Promise((res) => setTimeout(res, 150));

    await openCameraWithMode(newFacingMode);

    setIsSwitching(false);
  };

  const clearImage = () => {
    setImagePreview(null);
    setFileName('');
    setFileSize('');
    setState('idle');
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAnalyze = async () => {
    if (!imagePreview) return;

    try {
      setState('analyzing');

      // convert base64 to file
      const res = await fetch(imagePreview);
      const blob = await res.blob();
      const file = new File([blob], fileName || 'image.jpg', {
        type: blob.type,
      });

      // FormData (must be file)
      const formData = new FormData();
      formData.append('file', file);

      formData.append('save_history', String(saveHistory));

      const sessionId = localStorage.getItem("session_id");

      const response = await fetch(`${API_URL}/analyze`, {
        method: 'POST',
        headers: {
          "X-Session-ID": sessionId || "",
        },
        body: formData,
      });

      const data = await response.json();

      data.timestamp = new Date().toISOString();

      sessionStorage.setItem('analysis_result', JSON.stringify(data));

      // handle backend error
      if (data.status === 'error') {
        setResultStatus('fail');
        setState('done');
        return;
      }

      // success status
      setResultStatus('success');
      setState('done');

      // ui delay
      await new Promise((r) => setTimeout(r, 3000));

      // pass data
      sessionStorage.setItem('analysis_result', JSON.stringify(data));

      router.push('/results-dashboard');

    } catch (err) {
      console.error(err);
      setError('Something went wrong during analysis.');
      setState('error');
    }
  };

  const handleCancel = () => {
    closeCamera();
    setUploadSource(null);
    clearImage();
  };

  const handleRetry = () => {
    setError('');

    if (uploadSource === 'camera') {
      openCameraWithMode(facingMode);
      return;
    }

    if (uploadSource === 'file') {
      fileInputRef.current?.click();
      return;
    }

    clearImage();
  };

  const goToPrivacy = () => {
    if (privacyRef.current) {
      privacyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

      setHighlightPrivacy(true);
      setTimeout(() => setHighlightPrivacy(false), 1500);
    }
  };

  const goToTips = () => {
    clearImage();

    setTimeout(() => {
      if (tipsRef.current) {
        tipsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      setHighlightTips(true);
      setTimeout(() => setHighlightTips(false), 1500);
    }, 100);
  };


  // effects
  useEffect(() => {
    let sessionId = localStorage.getItem("session_id");

    if (!sessionId) {
      sessionId = crypto.randomUUID();
      localStorage.setItem("session_id", sessionId);
    }
  }, []);

  useEffect(() => {
    setFacingMode('environment');
  }, []);

  useEffect(() => {
    if (state === 'camera' && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [state, stream]);


  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev % 3) + 1);
    }, 400);

    return () => clearInterval(interval);
  }, []);


  return (
    <div className="flex-1 max-w-2xl w-full h-full">
      <div className="bg-white rounded-3xl shadow-medium border border-slate-100 overflow-hidden h-full flex flex-col">
       
        {/* header */}
        <div className="px-8 pt-8 pb-6 border-b border-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-2xl flex items-center justify-center">
                <ImageIcon size={18} className="text-blue-500" />
              </div>
              <div>
                <h2 className="font-bold text-slate-800 text-lg">Image Upload</h2>
                <p className="text-slate-400 text-xs">JPG, PNG, WebP · Max 10MB</p>
              </div>
            </div>
            {hasImage && !isAnalyzing && (
            <div className="relative group">
              <button
                onClick={clearImage}
                className="p-1 rounded-2xl bg-slate-100 text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <X size={18} />
              </button>

              {/* tooltip */}
              <div className="
                absolute right-0 mt-2
                px-2 py-1
                text-[10px] sm:text-xs
                text-white bg-slate-700/90
                rounded-md shadow-lg
                opacity-0 translate-y-1
                group-hover:opacity-100 group-hover:translate-y-0
                transition-all duration-200
                whitespace-nowrap
                pointer-events-none
              ">
                Remove Image
              </div>
            </div>
              
            )}
          </div>
        </div>

        {/* upload area */}
        <div className="p-8">

          {state === 'camera' && (
            <div className="relative">
              <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{aspectRatio: window.innerWidth < 640 ? '3/4' : '4/3'}}>
                
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                    className={`w-full h-full object-cover scale-[1.1] sm:scale-100 
                      ${facingMode === 'user' ? '-scale-x-100' : ''}
                    `}
                />

                {/* tongue guide overlay */}
                <div className="absolute inset-0 flex items-end sm:pb-20 pb-16 justify-center pointer-events-none">
                  <svg
                    viewBox="0 0 200 220"
                    className="w-[60%] sm:w-[45%] md:w-[45%] max-w-[280px] opacity-90"
                  >
                    <path
                      d="
                        M40 30
                        L40 150
                        Q100 240 160 150
                        L160 30
                      "
                      fill="none"
                      stroke="#38BDF8"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray="8 6"
                    />
                  </svg>
                </div>

                {/* instruction overlay */}
                <div className="absolute sm:bottom-16 bottom-12 w-full text-center pointer-events-none">
                  <p className="text-white/80 text-xs sm:text-sm font-medium">
                    Align your tongue within the guide
                  </p>
                </div>

                <canvas ref={canvasRef} className="hidden" />


                <div className="absolute top-5 right-4 sm:top-5 sm:right-5 flex items-center gap-2">
                  {/* switch camera button */}
                  <>
                    <div className="group">
                      <button
                        onClick={switchCamera}
                        className="
                          flex items-center justify-center
                          backdrop-blur-md bg-white/100
                          rounded-full
                          w-6 h-6 sm:w-6 sm:h-6
                          shadow-sm
                          hover:bg-blue-50 hover:text-blue-500
                          transition-all duration-200 hover:scale-110
                        "
                      >
                        <FlipCameraAndroidOutlinedIcon style={{ fontSize: 17 }} />
                      </button>

                      <div className="
                        absolute right-0 mt-1
                        px-2 py-1
                        text-[10px] sm:text-[11px]
                        text-white bg-slate-800/90
                        rounded-md shadow-lg
                        opacity-0 translate-y-1
                        group-hover:opacity-100 group-hover:translate-y-0
                        transition-all duration-200
                        whitespace-nowrap
                        pointer-events-none
                      ">
                        Switch Camera
                      </div>
                    </div>
                  </>

                  {/* close button */}
                  <div className="group">
                    <button
                      onClick={() => {
                        closeCamera();
                        setState('idle');
                      }}
                      className="
                        flex items-center justify-center
                        backdrop-blur-md bg-white/100
                        rounded-full
                        w-6 h-6 sm:w-6 sm:h-6
                        shadow-sm
                        hover:bg-red-50 hover:text-red-500
                        transition-all duration-200 hover:scale-110
                      "
                    >
                      <X className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>

                    <div className="
                      absolute right-0 mt-1
                      px-2 py-1
                      text-[10px] sm:text-[11px]
                      text-white bg-slate-800/90
                      rounded-md shadow-lg
                      opacity-0 translate-y-1
                      group-hover:opacity-100 group-hover:translate-y-0
                      transition-all duration-200
                      whitespace-nowrap
                      pointer-events-none
                    ">
                      Close Camera
                    </div>
                  </div>
                </div>


                {/* capture button */}
                <div className="absolute sm:bottom-4 bottom-4 left-1/2 -translate-x-1/2 group">
                  <button
                    onClick={capturePhoto}
                    className="flex items-center justify-center rounded-full bg-white shadow-lg border border-gray-200 w-6 h-6 sm:w-8 sm:h-8 hover:scale-105 hover:bg-blue-100 active:scale-95 transition-all duration-200 "
                  >
                    <CameraRoundedIcon className="w-6 h-6 sm:w-6 sm:h-6 text-gray-700" />
                  </button>

                  <div className="
                    absolute bottom-10 sm:bottom-10 left-1/2 -translate-x-1/2
                    px-2 py-1
                    text-[10px] sm:text-[11px]
                    text-white bg-slate-700/100
                    rounded-md shadow-lg
                    opacity-0 translate-y-2
                    group-hover:opacity-100 group-hover:translate-y-0
                    transition-all duration-200
                    whitespace-nowrap
                    pointer-events-none
                  ">
                    Capture Tongue Image
                  </div>
                </div>
              </div>
            </div>
          )}

          {(state === 'idle' || state === 'dragging' || state === 'error') && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-2xl transition-all duration-200 cursor-pointer min-h-[305px] flex flex-col items-center justify-center gap-4 ${
                state === 'dragging' ?'border-blue-400 bg-blue-50 scale-[1.01]'
                  : state === 'error' ?'border-red-300 bg-red-50' :'border-slate-200 bg-slate-50/50 hover:border-blue-300 hover:bg-blue-50/50'
              }`}
            >
              {/* animated upload icon */}
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-200 ${
                state === 'dragging' ? 'bg-blue-100 scale-110' : state === 'error' ? 'bg-red-100' : 'bg-white shadow-soft'
              }`}>
                {state === 'error' ? (
                  <AlertCircle size={32} className="text-red-400" />
                ) : (
                  <Upload size={32} className={`transition-colors duration-200 ${state === 'dragging' ? 'text-blue-500' : 'text-slate-400'}`} />
                )}
              </div>

              {state === 'error' ? (
                <div className="text-center">
                  <p className="text-red-600 font-semibold text-base mb-1">Upload Failed</p>
                  <p className="text-red-500 text-sm">{error}</p>
                  <p className="text-slate-400 text-xs mt-2">Click to try again</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className={`font-semibold text-base mb-1 transition-colors ${state === 'dragging' ? 'text-blue-600' : 'text-slate-700'}`}>
                    {state === 'dragging' ? 'Drop your image here' : 'Drop image or browse'}
                  </p>
                  <p className="text-slate-400 text-sm mb-4 px-3">
                    Drag and drop your tongue photo, or click to browse
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {['JPG', 'PNG', 'WebP'].map((fmt) => (
                      <span key={fmt} className="text-xs bg-white border border-slate-200 text-slate-500 rounded-full px-2.5 py-1 font-medium">
                        {fmt}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* drag visual overlay */}
              {state === 'dragging' && (
                <div className="absolute inset-0 rounded-2xl border-2 border-blue-400 bg-blue-100/30 animate-pulse pointer-events-none" />
              )}
            </div>
          )}

          {(state === 'preview' || state === 'analyzing' || state === 'done') && imagePreview && (
            <div className="relative">
              <div className="relative w-full min-h-[320px] sm:min-h-0 rounded-2xl sm:overflow-hidden overflow-visible isolate bg-slate-100" style={{aspectRatio: window.innerWidth < 640 ? '3/4' : '4/3'}}>
                <img
                  src={imagePreview!}
                  alt="Captured"
                  className="w-full h-full object-cover object-center rounded-2xl"
                />

                {/* ANALYZING overlay */}
                {isAnalyzing && (
                  <div className="
                    fixed inset-0 sm:absolute
                    z-50 sm:z-20
                    flex items-center justify-center
                    rounded-2xl 
                    px-4
                  ">

                    {/* bg overlay */}
                    <div className="absolute inset-0 sm:rounded-2xl bg-slate-900/50 backdrop-blur-md z-0" />

                    {/* scan line */}
                    <div className="absolute inset-0 rounded-2xl z-10 overflow-hidden">
                      <ScanLine />
                    </div>

                    {/* center card */}
                    <div className="absolute inset-0 flex items-center justify-center px-5 sm:px-6 md:px-8 z-20">
                      <div className="
                        w-full max-w-xs sm:max-w-sm
                        bg-white
                        rounded-2xl
                        px-4 py-5 sm:px-6 sm:py-6
                        text-center
                        shadow-xl
                      ">

                        {/* loader */}
                        <div className="flex justify-center mb-4">
                          <div className="relative w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">

                            <div className="absolute w-full h-full rounded-full border-4 border-blue-100" />

                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-[3px] border-blue-500 border-t-transparent animate-spin" />

                          </div>
                        </div>

                        <p className="text-gray-900 font-semibold text-sm sm:text-base mb-1">
                          Processing Your Screening
                        </p>

                        <p className="text-gray-500 text-xs sm:text-sm mb-3 leading-relaxed">
                          Analyzing your diabetes risk
                          <span className="inline-block w-[1.5em] text-left">
                            {".".repeat(dotCount)}
                          </span>
                        </p>

                        {/* progress bar */}
                        <div className="relative h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="absolute inset-0">
                            <div className="h-full w-1/4 sm:w-1/3 bg-blue-600 rounded-full animate-loading-bar" />
                          </div>
                        </div>

                        <p className="text-[11px] text-gray-400 mt-3">
                          This may take a few seconds — please wait.
                        </p>

                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setIsExpanded(true)}
                  className="
                    absolute top-3 right-3 z-30
                    p-2 rounded-full
                    bg-white/80 backdrop-blur
                    hover:bg-white
                    shadow-md
                    transition
                  "
                >
                  <Maximize2 size={16} />
                </button>

                {/* DONE overlay */}
                {state === 'done' && (
                  <div className={`
                    ${state === 'done' ? 'fixed inset-0 sm:absolute' : ''}
                    z-50 sm:z-20
                    flex items-center justify-center`}>

                    {/* bg overlay */}
                    <div className="fixed sm:absolute inset-0 sm:rounded-2xl bg-slate-900/50 backdrop-blur-md z-0" />

                    {/* center card */}
                    <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-6 sm:items-center sm:pt-0 pt-6">

                      <div className="
                        w-full max-w-xs sm:max-w-sm
                        bg-white
                        rounded-2xl
                        px-5 py-6
                        text-center
                        shadow-xl">

                        {/* successful detection overlay*/}
                        {resultStatus === 'success' ? (
                        <>
                            <div className="flex justify-center mb-5 p-3">
                              <div className="custom-loader"></div>
                            </div>

                            <p className="text-gray-900 font-semibold text-sm sm:text-base mb-1">
                              Processing your results
                              <span className="inline-block w-[1.5em] text-left">
                                {".".repeat(dotCount)}
                              </span>
                            </p>

                            <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
                              Finalizing your diabetes risk assessment.
                            </p>
                          </>
                        ) : (
                          
                        // unsuccessful detection overlay        
                        <>
                          {/* icon */}
                          <div className="mb-5 flex justify-center">
                            <div className="relative w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">

                              <div className="absolute inset-0 rounded-full bg-amber-400/10 blur-md scale-110" />

                              <div className="absolute inset-0 rounded-full border border-amber-200 animate-ring-breathe" />

                              <AlertCircle className="relative text-amber-400 w-6 h-6 sm:w-7 sm:h-7 animate-pulse-scale" />

                            </div>
                          </div>

                          {/* title */}
                          <p className="text-gray-900 font-semibold text-sm sm:text-base mb-1">
                            We couldn’t detect your tongue
                          </p>

                          {/* description */}
                          <p className="text-gray-500 text-xs sm:text-sm leading-relaxed mb-4 max-w-xs mx-auto">
                             Please make sure your tongue is clearly visible and well-lit before trying again later.
                          </p>

                          {/* tip card */}
                          <div className="mb-5">
                            <div className="
                              flex items-center justify-center 
                              gap-2
                              bg-blue-50/60
                              border border-blue-100
                              rounded-xl
                              pl-2 pr-3 py-2
                              text-xs sm:text-sm
                              text-slate-600
                              max-w-xs mx-auto
                            ">
                              <Lightbulb className="w-4 h-4 text-blue-500 mt-[2px] flex-shrink-0" />

                              <p className="text-xs text-slate-600 text-center">

                                {/* Mobile version */}
                                <span className="sm:hidden">
                                  Check the provided{' '}
                                  <span
                                    onClick={goToTips}
                                    className="text-blue-600 font-medium cursor-pointer hover:underline"
                                  >
                                    photography tips
                                  </span>.
                                </span>

                                {/* Desktop version */}
                                <span className="hidden sm:inline">
                                  Check the{' '}
                                  <span
                                    onClick={goToTips}
                                    className="text-blue-600 font-medium cursor-pointer hover:underline"
                                  >
                                    photography tips
                                  </span>{' '}
                                  for better results.
                                </span>

                              </p>

                            </div>
                          </div>

                          {/* buttons */}
                          <div className="flex flex-row gap-2 sm:gap-3 justify-center max-w-xs mx-auto">

                            <button
                              onClick={handleCancel}
                              className="
                                w-full sm:w-auto
                                px-3 py-2 sm:px-4 sm:py-2
                                rounded-xl
                                text-xs sm:text-sm font-medium
                                bg-white text-blue-600
                                border border-blue-200
                                hover:bg-blue-50
                                transition-all duration-200
                                hover:scale-105 active:scale-95
                              "
                            >
                              Cancel
                            </button>

                            <button
                              onClick={handleRetry}
                              className="
                                w-full sm:w-auto
                                flex items-center justify-center gap-2
                                px-3 py-2 sm:px-4 sm:py-2
                                rounded-xl
                                text-xs font-semibold
                                bg-gradient-to-r from-blue-500 to-blue-600 text-white
                                hover:from-blue-600 hover:to-blue-700
                                transition-all duration-200
                                hover:scale-105 active:scale-95
                                shadow-md hover:shadow-lg hover:shadow-blue-200/40
                              "
                            >
                              <RotateCcw className="w-3 h-3" />
                              Try Again
                            </button>

                          </div>

                          {/* footer */}
                          <div className="mt-5 flex items-center justify-center gap-1 text-[10px] sm:text-[11px] text-slate-400 max-w-xs mx-auto">
                            <ShieldCheck className="w-3 h-3 text-emerald-500" />
                            Your privacy is protected.
                          </div>
                        </>
                          
                        )}
                      </div>
                    </div>
                  </div>
                )}

              </div>

              {/* image metadata */}
              {!isAnalyzing && state !== 'done' && (
                <div className="mt-4 flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileImage size={14} className="text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{fileName}</p>
                    <p className="text-xs text-slate-400">{fileSize} · Ready for analysis</p>
                  </div>
                  <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                </div>
              )}
            </div>
          )}

          {/* action buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handleFileInput}
            />

            {!hasImage && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-full px-6 py-3.5 text-sm shadow-blue hover:shadow-blue-strong hover:from-blue-600 hover:to-blue-700 transition-all duration-200 active:scale-95"
                >
                  <Upload size={16} />
                  Browse Files
                </button>
                <button
                  className="flex-1 flex items-center justify-center gap-2 bg-white text-slate-600 font-semibold rounded-full px-6 py-3.5 text-sm border border-slate-200 shadow-soft hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 active:scale-95"
                  onClick={openCamera}
                >
                  <Camera size={16} />
                  Camera
                </button>
              </>
            )}

            {state === 'preview' && (
              <>
                <button
                  onClick={handleAnalyze}
                  className="
                    flex items-center justify-center gap-2
                    bg-gradient-to-r from-blue-500 to-blue-600 
                    text-white font-bold 
                    rounded-full px-10 py-4 text-base

                    mx-auto
                    w-full max-w-xs sm:w-fit 

                    shadow-blue hover:shadow-blue-strong 
                    hover:from-blue-600 hover:to-blue-700 
                    transition-all duration-200 active:scale-95
                  "                >
                  <Scan size={18} />
                  Analyze Image
                </button>
              </>
            )}

            {isAnalyzing && (
            <button
              disabled
              className="
                hidden sm:flex
                items-center justify-center gap-2
                bg-blue-100 text-blue-400 font-semibold 
                rounded-full px-10 py-4 text-base

                text-[9px] sm:text-sm

                mx-auto
                w-full max-w-xs sm:w-fit 

                cursor-not-allowed
              "
            >
              <Loader2 size={18} className="animate-spin" />
              Analyzing image...
            </button>
            )}
          </div>

          {/* disclaimer */}
          <p className="mt-4 text-center text-xs text-slate-400 leading-relaxed">
            {saveHistory ? (
              <>
                Your data may be securely stored for research purposes.{' '}
                <span
                  onClick={goToPrivacy}
                  className="text-blue-500 font-medium underline cursor-pointer hover:text-blue-600"
                >
                  Manage this in Privacy Settings.
                </span>
              </>
            ) : (
              <>
                Your data is processed for analysis only and not stored.{' '}
                <span
                  onClick={goToPrivacy}
                  className="text-blue-500 font-medium underline cursor-pointer hover:text-blue-600"
                >
                  Enable storage in Privacy Settings.
                </span>
              </>
            )}
          </p>
        </div>
      </div>


      {/* privacy section*/}
        <div
          ref={privacyRef}
          className={`mt-8 bg-white rounded-2xl p-8 border shadow-sm transition-all duration-500 ${
            highlightPrivacy
              ? 'border-blue-400 ring-2 ring-blue-200 scale-[1.02]'
              : 'border-slate-100'
          }`}
        >
            
            <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100">
              
              {/* header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-white p-2 rounded-lg text-blue-600 shadow-sm">
                  <Shield size={20} />
                </div>
                <h3 className="font-bold text-slate-900">
                  Privacy & Data Control
                </h3>
              </div>

              {/* content */}
              <div className="flex items-start gap-4">
                
                {/* checkbox */}
                <div className="flex items-center h-6 mt-0.5">
                {!isFullscreenOverlay && (
                  <button
                    type="button"
                    onClick={() => setSaveHistory(!saveHistory)}
                    className={`
                      w-5 h-5 rounded-md flex items-center justify-center
                      border transition-all duration-200 ease-out
                      focus:outline-none focus:ring-0
                      hover:scale-110
                      active:scale-90
                      ${saveHistory 
                        ? 'bg-blue-600 border-blue-600 shadow-sm scale-105' 
                        : 'bg-white border-slate-300 hover:border-blue-400'
                      }
                    `}
                  >
                    <Check
                      size={14}
                      className={`
                        text-white transition-all duration-200
                        ${saveHistory ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}
                      `}
                    />
                  </button>
                )}
                </div>

                {/* text */}
                <div className="space-y-2">
                  <label
                    htmlFor="save-history"
                    className="text-sm font-bold text-slate-900 block cursor-pointer"
                  >
                    Allow my data to be stored for research purposes
                  </label>

                  <p className="text-xs text-slate-500 leading-relaxed max-w-xl">
                    By enabling this option, your tongue image and analysis results may be securely stored to support future research and system improvement. 
                    All data is handled in accordance with privacy standards and used only for research and evaluation. 
                    If disabled, your data will be processed for analysis only and permanently deleted afterward.
                  </p>

                  {/* badge */}
                  <div className="pt-1">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100/50 text-blue-600 text-[10px] font-bold tracking-wider uppercase">
                      <ShieldCheck size={14} />
                      Securely processed
                    </span>
                  </div>
                </div>

              </div>
            </div>

        </div>

      {/* privacy section*/}
      {isExpanded && imagePreview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">

          {/* close button */}
          <button
            onClick={() => setIsExpanded(false)}
            className="absolute top-6 right-6 text-white"
          >
            <X size={28} />
          </button>

          {/* full image */}
          <img
            src={imagePreview}
            alt="Expanded"
            className="max-w-[80%] max-h-[80%] object-contain rounded-2xl"
          />
        </div>
      )}

    </div>
  );
}